import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { AnimatePresence, motion } from "framer-motion";

import InfoPage from "../../components/InfoPage/InfoPage";
import "./Contact.css";

const EMAILJS_SERVICE_ID = "service_izhp057";
const EMAILJS_TEMPLATE_ID = "template_ptxzg1c";
const EMAILJS_PUBLIC_KEY = "MW3vUckmfkK0t44mz";

function Contact() {
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const showMessage = status === "success" || status === "error";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formRef.current) return;

    try {
      setStatus("loading");

      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY,
      );

      setStatus("success");
      formRef.current.reset();

      setTimeout(() => {
        setStatus("idle");
      }, 4200);
      formRef.current.reset();
    } catch {
      setStatus("error");

      setTimeout(() => {
        setStatus("idle");
      }, 4200);
    }
  };

  return (
    <InfoPage
      title="Contact Us"
      subtitle={
        <>
          We are always ready to connect, collaborate, and create impact
          together.
        </>
      }
      className="contact-page"
    >
      <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
        <div className="contact-form__row">
          <input
            type="text"
            name="user_name"
            placeholder="Your name"
            required
          />
          <input
            type="email"
            name="user_email"
            placeholder="Your email"
            required
          />
        </div>

        <input type="text" name="subject" placeholder="Subject" required />

        <textarea name="message" placeholder="Your message" rows="5" required />

        <button type="submit" disabled={status === "loading"}>
          {status === "loading" ? "Sending..." : "Send Message"}
        </button>

        <AnimatePresence>
          {showMessage && (
            <motion.div
              className={`contact-toast contact-toast--${status}`}
              initial={{ opacity: 0, y: 16, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              role="status"
            >
              <span className="contact-toast__decor" aria-hidden="true" />

              <div>
                <strong>
                  {status === "success"
                    ? "Message sent successfully"
                    : "Something went wrong"}
                </strong>

                <p>
                  {status === "success"
                    ? "Thank you for reaching out. We will get back to you shortly."
                    : "Please check your connection and try again."}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <div className="contact-page__social-divider" aria-hidden="true">
        <span />
      </div>

      <div className="contact-page__socials" aria-label="Social links">
        <a
          href="https://www.linkedin.com/company/oscomp-ch/"
          target="_blank"
          rel="noreferrer"
          aria-label="LinkedIn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={18}
            height={18}
            viewBox="0 5 1036 990"
          >
            <path
              fill="currentColor"
              d="M0 120c0-33.334 11.667-60.834 35-82.5C58.333 15.833 88.667 5 126 5c36.667 0 66.333 10.666 89 32 23.333 22 35 50.666 35 86 0 32-11.333 58.666-34 80-23.333 22-54 33-92 33h-1c-36.667 0-66.333-11-89-33S0 153.333 0 120zm13 875V327h222v668H13zm345 0h222V622c0-23.334 2.667-41.334 8-54 9.333-22.667 23.5-41.834 42.5-57.5 19-15.667 42.833-23.5 71.5-23.5 74.667 0 112 50.333 112 151v357h222V612c0-98.667-23.333-173.5-70-224.5S857.667 311 781 311c-86 0-153 37-201 111v2h-1l1-2v-95H358c1.333 21.333 2 87.666 2 199 0 111.333-.667 267.666-2 469z"
            />
          </svg>
        </a>

        <a
          href="https://www.instagram.com/ostrovskyi.company?igsh=dnEzNXl2eGowaXph"
          target="_blank"
          rel="noreferrer"
          aria-label="Instagram"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            width={18}
            height={18}
          >
            <path
              fill="currentColor"
              d="M11 0H5a5 5 0 0 0-5 5v6a5 5 0 0 0 5 5h6a5 5 0 0 0 5-5V5a5 5 0 0 0-5-5zm3.5 11c0 1.93-1.57 3.5-3.5 3.5H5c-1.93 0-3.5-1.57-3.5-3.5V5c0-1.93 1.57-3.5 3.5-3.5h6c1.93 0 3.5 1.57 3.5 3.5v6z"
            />
            <path
              fill="currentColor"
              d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 6.5A2.503 2.503 0 0 1 5.5 8c0-1.379 1.122-2.5 2.5-2.5s2.5 1.121 2.5 2.5c0 1.378-1.122 2.5-2.5 2.5z"
            />
            <circle fill="currentColor" cx="12.3" cy="3.7" r=".533" />
          </svg>
        </a>
      </div>
    </InfoPage>
  );
}

export default Contact;
